if (!customElements.get('volume-pricing-selector')) {
  customElements.define(
    'volume-pricing-selector',
    class VolumePricingSelector extends HTMLElement {
      constructor() {
        super();
        this.options = this.querySelectorAll('.volume-option__input');
        this.variantsData = JSON.parse(this.dataset.variants || '{}');
        this.currentVariantId = this.dataset.currentVariantId;
        this.currentVariant = null;
      }

      variantChangeUnsubscriber = undefined;

      connectedCallback() {
        // Subscribe to variant changes
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const sectionId = this.dataset.sectionId;
          if (event.data.sectionId !== sectionId) return;
          
          if (event.data.variant) {
            this.currentVariantId = event.data.variant.id;
            this.currentVariant = event.data.variant;
            this.updatePrices(event.data.variant);
            // Also update main price display when variant changes
            this.updateMainPriceDisplay();
          }
        });

        // Listen for option selection changes
        this.options.forEach(option => {
          option.addEventListener('change', this.onOptionChange.bind(this));
        });

        // Initialize main price display with current selection
        this.initCurrentVariant();
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
      }

      initCurrentVariant() {
        // Get initial variant data from data attributes
        const price = parseInt(this.dataset.currentVariantPrice, 10);
        const compareAtPrice = this.dataset.currentVariantComparePrice ? parseInt(this.dataset.currentVariantComparePrice, 10) : null;
        
        if (price) {
          this.currentVariant = {
            id: this.currentVariantId,
            price: price,
            compare_at_price: compareAtPrice
          };
          
          // Update main price display on initial load if an option is checked
          const checkedOption = this.querySelector('.volume-option__input:checked');
          if (checkedOption) {
            this.updateMainPriceDisplay();
          }
        }
      }

      onOptionChange(event) {
        const selectedOption = event.target;
        const optionContainer = selectedOption.closest('.volume-option');
        
        if (!optionContainer) return;

        const quantity = parseInt(optionContainer.dataset.quantity, 10);
        const discount = parseInt(optionContainer.dataset.discount, 10) || 0;

        // Update main price display
        this.updateMainPriceDisplay(quantity, discount);
      }

      updateMainPriceDisplay(selectedQuantity = null, selectedDiscount = null) {
        const sectionId = this.dataset.sectionId;
        
        // Get selected option if not provided
        if (selectedQuantity === null) {
          const checkedOption = this.querySelector('.volume-option__input:checked');
          if (checkedOption) {
            const optionContainer = checkedOption.closest('.volume-option');
            selectedQuantity = parseInt(optionContainer.dataset.quantity, 10);
            selectedDiscount = parseInt(optionContainer.dataset.discount, 10) || 0;
          } else {
            return;
          }
        }

        // Get variant price data
        let unitPrice, compareAtPrice;
        if (this.currentVariant) {
          unitPrice = this.currentVariant.price;
          compareAtPrice = this.currentVariant.compare_at_price;
        } else {
          // Try to get from data attributes
          const priceContainer = document.querySelector(`#price-${sectionId}`);
          if (priceContainer) {
            const priceEl = priceContainer.querySelector('.price-item--regular, .price-item--sale');
            if (priceEl) {
              // Can't easily get raw cents, so we'll skip if no variant data
              return;
            }
          }
          return;
        }

        // Calculate prices
        const originalTotal = unitPrice * selectedQuantity;
        let totalPrice, compareTotal, savingsPercent;

        if (selectedDiscount > 0) {
          totalPrice = Math.round(originalTotal * (100 - selectedDiscount) / 100);
          compareTotal = originalTotal;
          savingsPercent = selectedDiscount;
        } else if (compareAtPrice && compareAtPrice > unitPrice) {
          totalPrice = originalTotal;
          compareTotal = compareAtPrice * selectedQuantity;
          savingsPercent = Math.round(((compareTotal - totalPrice) / compareTotal) * 100);
        } else {
          totalPrice = originalTotal;
          compareTotal = null;
          savingsPercent = 0;
        }

        // Update main product price
        this.updatePriceContainer(`#price-${sectionId}`, totalPrice, compareTotal, savingsPercent);
        
        // Update sticky add to cart price
        this.updatePriceContainer(`#price-${sectionId}-sticky`, totalPrice, compareTotal, savingsPercent);
      }

      updatePriceContainer(selector, totalPrice, compareTotal, savingsPercent) {
        const priceContainer = document.querySelector(selector);
        if (!priceContainer) return;

        const priceWrapper = priceContainer.querySelector('.price');
        
        // Update sale/regular price display
        const salePriceEl = priceContainer.querySelector('.price-item--sale');
        const regularPriceEl = priceContainer.querySelector('.price__regular .price-item--regular');
        const compareEl = priceContainer.querySelector('.price__sale .price-item--regular');
        const saleBadge = priceContainer.querySelector('.price__badge-sale');

        if (compareTotal && compareTotal > totalPrice) {
          // Show sale state
          if (priceWrapper) {
            priceWrapper.classList.add('price--on-sale');
            priceWrapper.classList.remove('price--no-compare');
          }
          
          if (salePriceEl) {
            salePriceEl.textContent = this.formatMoney(totalPrice);
          }
          
          if (compareEl) {
            compareEl.textContent = this.formatMoney(compareTotal);
          }

          if (regularPriceEl) {
            regularPriceEl.textContent = this.formatMoney(totalPrice);
          }

          // Update sale badge
          if (saleBadge) {
            saleBadge.textContent = `Save ${savingsPercent}%`;
          }
        } else {
          // Show regular state
          if (priceWrapper) {
            priceWrapper.classList.remove('price--on-sale');
          }
          
          if (regularPriceEl) {
            regularPriceEl.textContent = this.formatMoney(totalPrice);
          }
          
          if (salePriceEl) {
            salePriceEl.textContent = this.formatMoney(totalPrice);
          }
        }
      }

      updatePrices(variant) {
        const unitPrice = variant.price;
        const compareAtPrice = variant.compare_at_price;

        this.querySelectorAll('.volume-option').forEach((option) => {
          const quantity = parseInt(option.dataset.quantity, 10);
          const discountPercent = parseInt(option.dataset.discount, 10) || 0;
          
          const originalTotal = unitPrice * quantity;
          let totalPrice, compareTotal, savingsPercent;
          
          if (discountPercent > 0) {
            // Use setting-based discount
            totalPrice = Math.round(originalTotal * (100 - discountPercent) / 100);
            compareTotal = originalTotal;
            savingsPercent = discountPercent;
          } else if (compareAtPrice && compareAtPrice > unitPrice) {
            // Fall back to compare_at_price
            totalPrice = originalTotal;
            compareTotal = compareAtPrice * quantity;
            savingsPercent = Math.round(((compareTotal - totalPrice) / compareTotal) * 100);
          } else {
            // No discount
            totalPrice = originalTotal;
            compareTotal = null;
            savingsPercent = 0;
          }

          // Update total price
          const totalPriceEl = option.querySelector('[data-total-price]');
          if (totalPriceEl) {
            totalPriceEl.textContent = this.formatMoney(totalPrice);
          }

          // Update compare price
          const comparePriceEl = option.querySelector('[data-compare-price]');
          if (comparePriceEl) {
            if (compareTotal && compareTotal > totalPrice) {
              comparePriceEl.textContent = this.formatMoney(compareTotal);
              comparePriceEl.classList.remove('hidden');
            } else {
              comparePriceEl.classList.add('hidden');
            }
          }

          // Update savings badge with percentage
          const savingsBadge = option.querySelector('[data-savings-badge]');
          if (savingsBadge) {
            if (savingsPercent > 0) {
              savingsBadge.textContent = `Save ${savingsPercent}%`;
              savingsBadge.classList.remove('hidden');
            } else {
              savingsBadge.classList.add('hidden');
            }
          }
        });
      }

      formatMoney(cents) {
        if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
          return Shopify.formatMoney(cents, this.dataset.moneyFormat || '');
        }
        // Fallback formatting
        const amount = (cents / 100).toFixed(2);
        const currencySymbol = this.dataset.currencySymbol || '$';
        return `${currencySymbol}${amount}`;
      }
    }
  );
}
